import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Edition = makeShortcode("Edition");
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "bulk-endpoints"
    }}>{`Bulk endpoints`}</h1>
    <p>{`Bulk API endpoints differ from other REST endpoints in that they combine multiple calls of the same type into an array and execute them as a single request. The endpoint handler splits the array into individual entities and writes them as separate messages to the message queue.`}</p>
    <p>{`​`}<Edition name="saas" mdxType="Edition" />{` In Adobe Commerce as a Cloud Service, message queues run automatically. There is no need to manage queues or install a message broker.`}</p>
    <p>{`​`}<Edition name="paas" mdxType="Edition" />{` Cron jobs are the default mechanism for `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-operations/configuration-guide/message-queues/manage-message-queues"
      }}>{`managing message queues`}</a>{` and starting message queue `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-operations/configuration-guide/message-queues/consumers"
      }}>{`consumers`}</a>{`, but you can also use external process control systems (like `}<a parentName="p" {...{
        "href": "https://supervisord.readthedocs.io/en/latest/"
      }}>{`Supervisor`}</a>{`) to monitor process management. You can use the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/commerce-operations/configuration-guide/cli/start-message-queues.html"
      }}><inlineCode parentName="a">{`bin/magento queue:consumers:start async.operations.all`}</inlineCode></a>{` command to manually start the `}<inlineCode parentName="p">{`async.operations.all`}</inlineCode>{` consumer that handles asynchronous and bulk API messages. However, manually starting consumers is not recommended because it requires you to keep your terminal session connected.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Before using the Bulk API to process messages, you must install and configure RabbitMQ, which is the default message broker. See `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-cloud-service/user-guide/configure/service/rabbitmq"
      }}>{`Setup RabbitMQ service`}</a>{` for cloud infrastructure projects and `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/commerce-operations/installation-guide/prerequisites/rabbitmq.html"
      }}>{`Message broker`}</a>{` for on-premises projects.`}</p>
    <h2 {...{
      "id": "routes"
    }}>{`Routes`}</h2>
    <p>{`The route to bulk calls varies between platforms:`}</p>
    <p>{`​`}<Edition name="paas" mdxType="Edition" />{` In Adobe Commerce on Cloud and on-premises projects, the route contains the prefix `}<inlineCode parentName="p">{`/async/bulk`}</inlineCode>{`, added before `}<inlineCode parentName="p">{`/V1`}</inlineCode>{` of a standard synchronous endpoint. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http"
      }}>{`POST https://<host>/rest/<store-view-code>/async/bulk/V1/products
PUT https://<host>/rest/<store-view-code>/async/bulk/V1/products/:sku
`}</code></pre>
    <p>{`​`}<Edition name="saas" mdxType="Edition" />{` In Adobe Commerce as a Cloud Service, the `}<inlineCode parentName="p">{`/async/bulk`}</inlineCode>{` segment occurs after the `}<inlineCode parentName="p">{`V1`}</inlineCode>{` segment of the route. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http"
      }}>{`POST https://<server>.api.commerce.adobe.com/<tenant-id>/V1/async/bulk/products
PUT https://<server>.api.commerce.adobe.com/<tenant-id>/V1/async/bulk/products/:sku
`}</code></pre>
    <p>{`Endpoint routes that contain input parameters require additional changes. For example, `}<inlineCode parentName="p">{`PUT /V1/products/:sku/media/:entryId`}</inlineCode>{` contains the `}<inlineCode parentName="p">{`:sku`}</inlineCode>{` and `}<inlineCode parentName="p">{`:entryId`}</inlineCode>{` input parameters. The route of a bulk request cannot contain input parameters, so you must change the route so that it does not contain any. To do this, replace the colon (`}<inlineCode parentName="p">{`:`}</inlineCode>{`) with `}<inlineCode parentName="p">{`by`}</inlineCode>{` and change the first letter of the parameter to uppercase.`}</p>
    <p>{`The following table provides several examples:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Platform`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Synchronous route`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Bulk route`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PaaS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PUT <path>/V1/products/:sku/media/:entryId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PUT <path>/async/bulk/V1/products/bySku/media/byEntryId`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PaaS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`POST <path>/V1/carts/:quoteId/items`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`POST <path>/async/bulk/V1/carts/byQuoteId/items`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SaaS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PUT <path>/V1/products/:sku/media/:entryId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PUT <path>/V1/async/bulk/products/bySku/media/byEntryId`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SaaS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`POST <path>/V1/carts/:quoteId/items`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`POST <path>/V1/async/bulk/carts/byQuoteId/items`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`GET requests are not supported.`}</p>
    <h2 {...{
      "id": "payloads"
    }}>{`Payloads`}</h2>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The following example is PaaS-specific. Adobe Commerce as a Cloud Service does not support the `}<inlineCode parentName="p">{`POST V1/customers`}</inlineCode>{` endpoint. However, the payload of any bulk requests can contain an array of objects.`}</p>
    <p>{`The payload of a bulk request contains an array of request bodies. For example, the minimal payload for creating four customers with `}<inlineCode parentName="p">{`POST <path>/async/bulk/V1/customers`}</inlineCode>{` would be structured as follows:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`[{
    "customer": {
        "email": "mshaw@example.com",
        "firstname": "Melanie Shaw",
        "lastname": "Doe"
    },
    "password": "Strong-Password"
},
{
    "customer": {
        "email": "bmartin@example.com",
        "firstname": "Bryce",
        "lastname": "Martin"
    },
    "password": "Strong-Password"
},
{
    "customer": {
        "email": "bmartin@example.com",
        "firstname": "Bryce",
        "lastname": "Martin"
    },
    "password": "Strong-Password"
},
{
    "customer": {
        "email": "tgomez@example.com",
        "firstname": "Teresa",
        "lastname": "Gomez"
    },
    "password": "Strong-Password"
}
]
`}</code></pre>
    <InlineAlert variant="success" slots="text" mdxType="InlineAlert" />
    <p>{`The second and third requests are duplicates.`}</p>
    <h2 {...{
      "id": "responses"
    }}>{`Responses`}</h2>
    <p>{`The response contains an array that indicates whether the call successfully added each request to the message queue. Although the duplicated request to create a customer will fail, Commerce added it to the message queue successfully.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "bulk_uuid": "799a59c0-09ca-4d60-b432-2953986c1c38",
    "request_items": [
        {
            "id": 0,
            "data_hash": "3d3d853839dd442d0b99a1badea756a03f19ffb0fb7aab672c05f83d5a914181",
            "status": "accepted"
        },
        {
            "id": 1,
            "data_hash": "bf8859d03545f0fa80084a47348a629cdf571fc064b952e7396c338d5cf3bf6e",
            "status": "accepted"
        },
        {
            "id": 2,
            "data_hash": "876f3f2e4b226d54dcbf3f5ce752a9f748a45310261d2dd5cc7a7c9ef74b4369",
            "status": "accepted"
        },
        {
            "id": 3,
            "data_hash": "9c1bd4bfd8defcc856ddf129cc01d172625d139d5f7dcf53b6cb09a0e9a843a3",
            "status": "accepted"
        }
    ],
    "errors": false
}
`}</code></pre>
    <h2 {...{
      "id": "delete-requests"
    }}>{`DELETE requests`}</h2>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The following example is PaaS-specific. Adobe Commerce as a Cloud Service does not support the `}<inlineCode parentName="p">{`DELETE V1/cmsPage`}</inlineCode>{` endpoint or its bulk and asynchronous counterparts.`}</p>
    <p>{`The following call asynchronously deletes CMS blocks with IDs `}<inlineCode parentName="p">{`1`}</inlineCode>{` and `}<inlineCode parentName="p">{`2`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http"
      }}>{`DELETE <server-path>/async/bulk/V1/cmsPage/byPageId
`}</code></pre>
    <h3 {...{
      "id": "delete-request-payload"
    }}>{`DELETE request payload`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`[
    {
        "pageId": "1"
    },
    {
        "pageId": "2"
    }
]
`}</code></pre>
    <h2 {...{
      "id": "store-scopes"
    }}>{`Store scopes`}</h2>
    <edition name="paas" />
    <p>{`You can specify a store code (which is labeled in the Admin as store view code) in the route of an asynchronous endpoint so that it operates on a specific store, as shown below:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http"
      }}>{`POST /<store_code>/async/bulk/V1/products
PUT /<store_code>/async/bulk/V1/products/bySku
`}</code></pre>
    <p>{`As a result, the asynchronous calls update the products on the specific store, instead of the default store.`}</p>
    <p>{`You can specify the `}<inlineCode parentName="p">{`all`}</inlineCode>{` store code to perform operations on all existing stores:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-http"
      }}>{`POST /all/async/bulk/V1/products
PUT /all/async/bulk/V1/products/bySku
`}</code></pre>
    <h2 {...{
      "id": "fallback-and-creatingupdating-objects-when-setting-store-scopes"
    }}>{`Fallback and creating/updating objects when setting store scopes`}</h2>
    <edition name="paas" />
    <p>{`When you create or update an object, such as a product, you can specify the store code in the request. If you do not specify a store code, Commerce uses the default store scope.
The following rules apply when you create or update an object, such as a product.`}</p>
    <ul>
      <li parentName="ul">{`If you do not set the store code while creating a new product, Commerce creates a new object with all values set globally for each scope.`}</li>
      <li parentName="ul">{`If you do not set the store code while updating a product, then by fallback, Commerce updates values for the default store only.`}</li>
      <li parentName="ul">{`If you include the `}<inlineCode parentName="li">{`all`}</inlineCode>{` parameter, then Commerce updates values for all store scopes (in case a particular store doesn't yet have its own value set).`}</li>
      <li parentName="ul">{`If `}<inlineCode parentName="li">{`<store_code>`}</inlineCode>{` parameter is set, then values for only defined store will be updated.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      